import React, { useEffect } from "react";
import { FiCalendar, FiCreditCard, FiEdit3, FiLock, FiUser } from "react-icons/fi";
import AppCard from "./components/AppCard";
import ChildHeaderCard from "./components/ChildHeaderCard";
import StickyActionBar from "./components/StickyActionBar";
import {
  formatDate,
  getBirthDateValue,
  getChildKey,
  maskBsn,
} from "./utils/childUtils";

const CalendarIcon = FiCalendar;
const CardIcon = FiCreditCard;
const UserIcon = FiUser;
const EditIcon = FiEdit3;
const LockIcon = FiLock;

const rowInputClassName =
  "mt-2 h-11 w-full min-w-0 rounded-xl border border-emerald-600/35 bg-white/80 px-3 text-[0.96rem] font-semibold leading-snug text-slate-700 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-emerald-600 focus:bg-white focus:ring-4 focus:ring-emerald-600/10 dark:border-emerald-800 dark:bg-slate-900/80 dark:text-slate-100 dark:focus:bg-slate-900 dark:focus:ring-emerald-900/40";

const PersonalEditRow = ({
  t,
  icon,
  label,
  children,
  hint,
  editable = false,
  isLast = false,
  lockedLabel,
}) => (
  <div
    className={`grid grid-cols-[3rem_minmax(0,1fr)] gap-3 px-4 py-4 ${
      isLast ? "" : "border-b border-slate-200/80 dark:border-slate-700/80"
    } ${editable ? "bg-emerald-50/30 dark:bg-emerald-950/20" : "bg-slate-50/35 dark:bg-slate-900/35"}`}
  >
    <span
      className={`mt-0.5 flex h-10 w-10 items-center justify-center rounded-full ${
        editable
          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
          : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400"
      }`}
      aria-hidden="true"
    >
      {icon}
    </span>

    <div className="min-w-0">
      <div className="flex items-center justify-between gap-3">
        <div className="text-[0.95rem] font-black leading-tight text-slate-950 dark:text-slate-50">
          {label}
        </div>
        {editable ? (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-1 text-[0.68rem] font-black uppercase tracking-wide text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300">
            <EditIcon className="h-3 w-3" aria-hidden="true" />
            {t("common.editable", { defaultValue: "Editable" })}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-200/75 px-2 py-1 text-[0.68rem] font-black uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            <LockIcon className="h-3 w-3" aria-hidden="true" />
            {lockedLabel || "Locked"}
          </span>
        )}
      </div>
      {children}
      {hint && (
        <div className="mt-1.5 text-[0.76rem] font-semibold leading-snug text-slate-500 dark:text-slate-400">
          {hint}
        </div>
      )}
    </div>
  </div>
);

const ChildPersonalEdit = ({
  t,
  child,
  classroomsByChild = {},
  childInfoEdit,
  startChildInfoEdit,
  updateChildInfoDraft,
  cancelChildInfoEdit,
  submitChildInfoEdit,
  pendingChildren = [],
  pendingChildUpdateSet = new Set(),
  onBack,
  onSaved,
}) => {
  const key = getChildKey(child);
  const editState = childInfoEdit?.[key];

  useEffect(() => {
    if (!editState?.editing && typeof startChildInfoEdit === "function") {
      startChildInfoEdit(child);
    }
  }, [child, editState?.editing, startChildInfoEdit]);

  const draftFirstName = editState?.draftFirstName ?? child?.firstName ?? "";
  const draftLastName = editState?.draftLastName ?? child?.lastName ?? "";
  const draftBsn = editState?.draftBsn ?? child?.bsn ?? "";
  const bsnEditable = !child?.bsn;

  const handleSubmit = async () => {
    await submitChildInfoEdit?.(child);
    onSaved?.();
  };

  const handleCancel = () => {
    cancelChildInfoEdit?.(child.id);
    onBack?.();
  };

  return (
    <div className="pp-family-view mx-auto grid w-full max-w-[33rem] gap-4 pb-28">
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 px-1 pt-1">
        <button
          type="button"
          onClick={handleCancel}
          className="inline-flex w-fit items-center gap-2 pt-3 text-[0.92rem] font-black text-emerald-700"
        >
          <span className="text-xl leading-none" aria-hidden="true">
            ‹
          </span>
          {t("common.back", { defaultValue: "Back" })}
        </button>
        <ChildHeaderCard
          t={t}
          child={child}
          classroomsByChild={classroomsByChild}
          pendingChildren={pendingChildren}
          pendingChildUpdateSet={pendingChildUpdateSet}
        />
      </div>

      <div className="px-1">
        <h2 className="m-0 text-[1.45rem] font-black leading-tight text-slate-950 dark:text-slate-50">
          {t("parentPortal.children.edit_personal_details", {
            defaultValue: "Personal details",
          })}
        </h2>
        <p className="mt-2 text-[0.92rem] font-semibold leading-relaxed text-slate-600 dark:text-slate-300">
          {t("parentPortal.children.edit_personal_details_hint", {
            defaultValue: "You can edit the fields marked as editable.",
          })}
        </p>
      </div>

      <AppCard className="overflow-hidden">
        <PersonalEditRow
          t={t}
          icon={<UserIcon className="h-5 w-5" />}
          label={t("children.common.firstName", { defaultValue: "First name" })}
          editable
        >
          <input
            value={draftFirstName}
            onChange={(event) =>
              updateChildInfoDraft?.(child.id, "firstName", event.target.value)
            }
            className={rowInputClassName}
            autoComplete="given-name"
          />
        </PersonalEditRow>

        <PersonalEditRow
          t={t}
          icon={<UserIcon className="h-5 w-5" />}
          label={t("children.common.lastName", { defaultValue: "Last name" })}
          editable
        >
          <input
            value={draftLastName}
            onChange={(event) =>
              updateChildInfoDraft?.(child.id, "lastName", event.target.value)
            }
            className={rowInputClassName}
            autoComplete="family-name"
          />
        </PersonalEditRow>

        <PersonalEditRow
          t={t}
          icon={<CalendarIcon className="h-5 w-5" />}
          label={t("parentPortal.children.birth_date", {
            defaultValue: "Date of birth",
          })}
          lockedLabel={t("common.readOnly", { defaultValue: "Read only" })}
        >
          <span className="mt-2 block min-w-0 truncate rounded-xl border border-slate-200 bg-slate-100/80 px-3 py-2.5 text-[0.96rem] font-semibold leading-snug text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
            {formatDate(getBirthDateValue(child)) || "—"}
          </span>
        </PersonalEditRow>

        <PersonalEditRow
          t={t}
          icon={<CardIcon className="h-5 w-5" />}
          label={t("children.common.bsn", { defaultValue: "BSN" })}
          hint={
            bsnEditable
              ? t("parentPortal.child_info.bsn_visibility_hint", {
                  defaultValue: "Only visible to the school.",
                })
              : t("parentPortal.child_info.bsn_locked", {
                  defaultValue: "BSN is fixed",
                })
          }
          editable={bsnEditable}
          lockedLabel={t("common.fixed", { defaultValue: "Fixed" })}
          isLast={!editState?.error}
        >
          {bsnEditable ? (
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                value={draftBsn}
                onChange={(event) =>
                  updateChildInfoDraft?.(child.id, "bsn", event.target.value)
                }
                inputMode="text"
                className={`${rowInputClassName.replace("mt-2 ", "")} min-w-0 flex-1`}
              />
              <button
                type="button"
                onClick={() => updateChildInfoDraft?.(child.id, "bsn", "-")}
                className={`inline-flex min-h-11 shrink-0 items-center justify-center rounded-xl border px-3 text-[0.84rem] font-black transition ${
                  draftBsn === "-"
                    ? "border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-200"
                    : "border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300"
                }`}
              >
                {t("parentPortal.child_info.do_not_share_bsn", { defaultValue: "Do not share BSN" })}
              </button>
            </div>
          ) : (
            <span className="mt-2 block min-w-0 truncate rounded-xl border border-slate-200 bg-slate-100/80 px-3 py-2.5 text-[0.96rem] font-semibold leading-snug text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
              {maskBsn(child?.bsn)}
            </span>
          )}
        </PersonalEditRow>

        {editState?.error && (
          <p className="m-0 border-t border-slate-200/80 bg-red-50/90 px-4 py-3 text-[0.85rem] font-semibold text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300">
            {editState.error}
          </p>
        )}
      </AppCard>

      <StickyActionBar
        t={t}
        saving={editState?.saving}
        onCancel={handleCancel}
        onSave={handleSubmit}
      />
    </div>
  );
};

export default ChildPersonalEdit;
